import type { Mutation, QueryClient } from '@tanstack/react-query'

/**
 * Keeps a reload the server answered before a save was stored from replacing what that save put
 * on screen (USA-1672).
 *
 * Saves show their result at once (an optimistic update) and reload when they finish. A reload
 * started any other way while a save is in flight (a background-task event, polling, another
 * save finishing) can be answered before the save is stored. Without this guard its old values
 * replaced the saved ones, and a switch re-enabled showing the old value: the next click sent
 * the same value again.
 *
 * A query written while a save is in flight remembers that save. A reload of that query that
 * started before the save was stored is set aside: the screen keeps what it showed, and the
 * query reloads once every such save is stored.
 */
export function createSaveOrderGuard() {
  let clock = 0
  // When each save was stored (or refused), by mutation id.
  const storedAt = new Map<number, number>()
  // The saves in flight when each query was last written, by query hash.
  const writers = new Map<string, Set<number>>()
  const reloadStartedAt = new Map<string, number>()
  const shown = new Map<string, unknown>()
  const reloadWhenStored = new Set<string>()
  let client: QueryClient | undefined

  const unstoredSince = (hash: string, startedAt: number) =>
    [...(writers.get(hash) ?? [])].some((id) => (storedAt.get(id) ?? Infinity) > startedAt)

  const reload = (hash: string) => {
    const query = client?.getQueryCache().get(hash)
    if (query) void client?.invalidateQueries({ queryKey: query.queryKey, exact: true })
  }

  /** Call when a save's request finishes, before its own callbacks run. */
  const stored = (mutation: Mutation<unknown, unknown, unknown, unknown>) => {
    storedAt.set(mutation.mutationId, ++clock)
    for (const hash of [...reloadWhenStored]) {
      if (!unstoredSince(hash, clock)) {
        reloadWhenStored.delete(hash)
        reload(hash)
      }
    }
  }

  const install = (queryClient: QueryClient) => {
    client = queryClient
    const mutations = queryClient.getMutationCache()
    return queryClient.getQueryCache().subscribe((event) => {
      const hash = event.query.queryHash
      if (event.type === 'removed') {
        writers.delete(hash)
        reloadStartedAt.delete(hash)
        shown.delete(hash)
        reloadWhenStored.delete(hash)
        return
      }
      if (event.type !== 'updated') return
      const { action } = event
      if (action.type === 'fetch') {
        reloadStartedAt.set(hash, ++clock)
        return
      }
      if (action.type !== 'success') return
      if (action.manual) {
        const inFlight = mutations.getAll().filter((m) => m.state.status === 'pending')
        if (inFlight.length) {
          const ids = writers.get(hash) ?? new Set<number>()
          for (const mutation of inFlight) ids.add(mutation.mutationId)
          writers.set(hash, ids)
        }
        shown.set(hash, action.data)
        return
      }
      const startedAt = reloadStartedAt.get(hash) ?? 0
      if (unstoredSince(hash, startedAt)) {
        // Answered before a save it raced was stored: keep what the screen shows.
        queryClient.setQueryData(event.query.queryKey, shown.get(hash))
        if ([...(writers.get(hash) ?? [])].every((id) => storedAt.has(id))) reload(hash)
        else reloadWhenStored.add(hash)
        return
      }
      shown.set(hash, action.data)
      // Saves stored before this reload started are reflected in it.
      const ids = writers.get(hash)
      if (ids) {
        for (const id of ids) if ((storedAt.get(id) ?? Infinity) < startedAt) ids.delete(id)
        if (!ids.size) writers.delete(hash)
      }
    })
  }

  return { stored, install }
}
